// encounter-mutation-generation.ts — per-encounter count of WIRE-ORIGIN encounter state frames
// (fix-bug v2-ef 2026-10-14, "stale encounter fetch overwrites a newer WS change").
//
// getEncounterArr's network leg (template.js fetchEncounterArrFromServer) awaits the fetch AND the
// browser adoption (ftAdoptInventoryBase — seconds under the browser compile) before it installs the
// result in FateTide.encounterCache. A WS frame for that encounter arriving inside that window patches
// nothing (there is no cached entry yet, or the response overwrites the patched one), so the older
// response became cache truth for every consumer. The fetch now captures this counter at start and,
// if it moved, does not install the response: it fetches ONCE more and accepts that (bounded).
//
// ⛔ Only the router's state-frame entry bumps (router.ts) — never the shared adopt path: a browser-
// compiled re-adoption (C5's compiled handleEncounterUpdate branch) or the fetch's own install must not
// move the counter, or every compile would invalidate the in-flight fetch.

const generations = new Map<string, number>();
// Wire frames whose encounter cannot be resolved at router entry (a caster delta for a caster this page has not cached
// yet) count against EVERY encounter: a needless single refetch beats installing an answer the frame overtook.
let unresolvedGeneration = 0;

function key(encounterId: unknown): string {
    return String(encounterId ?? '').trim().toUpperCase();
}

export function bumpEncounterMutationGeneration(encounterId: unknown): void {
    const id = key(encounterId);
    if (!id) return;
    generations.set(id, (generations.get(id) ?? 0) + 1);
}

export function encounterMutationGeneration(encounterId: unknown): number {
    return (generations.get(key(encounterId)) ?? 0) + unresolvedGeneration;
}

type WireStateFrame = {
    type?: string;
    encounter_id?: unknown;
    encounter_arr?: unknown;
    caster_arr?: unknown;
    caster_id?: unknown;
    local?: unknown;
};

function scalar(value: unknown): unknown {
    let current = value;
    while (Array.isArray(current)) current = current[0];
    return current;
}

function bucketField(arr: unknown, bucket: string, field: string): unknown {
    if (!arr || typeof arr !== 'object') return undefined;
    const record = arr as Record<string, unknown>;
    const holder = record[bucket] && typeof record[bucket] === 'object' ? record[bucket] as Record<string, unknown> : record;
    return scalar(holder[field]);
}

/** The encounter a wire state frame mutates, or '' when the frame does not name one. */
export function wireFrameEncounterId(frame: WireStateFrame, casterEncounterId?: (casterId: string) => unknown): string {
    switch (frame.type) {
        case 'encounter_update':
            return key(frame.encounter_id ?? bucketField(frame.encounter_arr, 'encounters', 'encounter_id'));
        case 'encounter_update_delta':
        case 'encounter_stats_update':
        case 'base_patch':
            return key(frame.encounter_id);
        case 'caster_update':
            // A `local` frame is this browser's own render of a row it already holds — not wire truth.
            if (frame.local === true) return '';
            return key(bucketField(frame.caster_arr, 'encounter_creatures', 'encounter_id'));
        case 'caster_update_delta':
            return key(casterEncounterId?.(String(frame.caster_id ?? '')));
        default:
            return '';
    }
}

/** Router entry: count a wire state frame against the encounter it mutates. */
export function noteWireEncounterFrame(frame: WireStateFrame, casterEncounterId?: (casterId: string) => unknown): void {
    const id = wireFrameEncounterId(frame, casterEncounterId);
    if (id) bumpEncounterMutationGeneration(id);
    // A caster delta always mutates SOME row; unresolved → it may be the one an in-flight fetch is about to install.
    else if (frame.type === 'caster_update_delta') unresolvedGeneration += 1;
}

/**
 * Fetch + adopt an encounter, refusing a response that a wire frame may have overtaken. `fetchAndAdopt` resolves the
 * adopted arr (or null on failure). If this encounter's counter moved during the attempt, the detached result is discarded
 * and ONE more attempt is made (including a failed attempt). Catalog preload may instead join a newer valid canonical
 * paint: that answer is already current and must not be discarded. `onStale` is diagnostic only.
 */
export async function fetchEncounterWithMutationGuard<T>(
    encounterId: unknown,
    fetchAndAdopt: () => Promise<T | null>,
    onStale?: (detail: { encounterId: string; before: number; after: number }) => void,
): Promise<T | null> {
    const before = encounterMutationGeneration(encounterId);
    const first = await fetchAndAdopt();
    const after = encounterMutationGeneration(encounterId);
    if (after === before) return first;
    if (typeof window !== 'undefined') {
        if (first !== null && first === window.FateTide?.encounterCache?.get(key(encounterId))) return first;
        if (first !== null) window.ftDiscardInventoryBase?.(first);
    }
    onStale?.({ encounterId: key(encounterId), before, after });
    return fetchAndAdopt();
}

if (typeof window !== 'undefined') {
    window.ftEncounterMutationGeneration = encounterMutationGeneration;
    window.ftFetchEncounterWithMutationGuard = fetchEncounterWithMutationGuard;
}
